/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import AutosuggestService from 'cio/services/autosuggest';
import { TaskInstance } from 'ember-concurrency';
import DeviceAttributeCondition, {
  getSpecialDeviceAttributeConfig,
  SpecialDeviceAttributes,
} from 'cio/app-models/conditions/device-attribute';
import { existenceOperators } from 'cio/utils/operators';
import DevicesService from 'cio/services/devices';
import { action, computed } from '@ember/object';

interface DeviceModelValueArgs {
  condition: DeviceAttributeCondition;
  disabled: boolean;
  size: string;
}

export default class DeviceModelValue extends Component<DeviceModelValueArgs> {
  @service devices!: DevicesService;
  @service autosuggest!: AutosuggestService;

  @tracked deviceMarketName = '';
  @tracked autosuggestTaskInstance: TaskInstance<unknown> | null = null;
  @tracked isLoading = true;

  constructor(owner: unknown, args: DeviceModelValueArgs) {
    super(owner, args);
    this.init();
  }

  get isLoadingValues() {
    return this.isLoading || this.autosuggestTaskInstance?.isRunning;
  }

  @computed('args.condition.operator')
  get isExistenceCondition() {
    return existenceOperators.some((op) => op.value === this.args.condition.operator);
  }

  get deviceModelsAutosuggestTaskInstanceValues() {
    return (this.autosuggestTaskInstance?.value as string[] | undefined) ?? [];
  }

  get autocompleteItems() {
    let items: string[] = [];

    this.deviceModelsAutosuggestTaskInstanceValues.forEach((item) => {
      items.push(this.devices.getMarketingName(item) ?? item);
    });

    return items;
  }

  @action
  onValueChange(newValue: string) {
    this.deviceMarketName = newValue;
    let deviceModel = this.deviceModelsAutosuggestTaskInstanceValues.find((item) => {
      return this.devices.getMarketingName(item) === newValue;
    });
    this.args.condition.set('value', deviceModel ?? newValue);
  }

  private async init() {
    // load autosuggest values
    this.autosuggestTaskInstance = this.autosuggest.loadDeviceAttributeValues.perform(this.args.condition.name);

    await this.devices.load.perform();

    let { value, operator } = this.args.condition;
    this.deviceMarketName = this.devices.getMarketingName(value);
    if (this.isDeprecatedOperator(operator)) {
      this.args.condition.set('operator', 'eq');
    }

    this.isLoading = false;
  }

  private isDeprecatedOperator(operator: string) {
    let config = getSpecialDeviceAttributeConfig(SpecialDeviceAttributes.device_model);
    return config?.operators?.some((op) => op.value === operator) === false;
  }
}
